/* 
 * Copyright (C) 2022 SHC Holdings LLC.  All Rights Reserved. 
 */
import React, { useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TabPane, Label, FormGroup } from 'reactstrap';

import {
  Button,
  DataTable,
  FallbackComponent,
  SimpleCheckbox,
  LookupVaccine,
  useLookupVaccine,
  FilterDialogTabItemProps,
  useFilterEditingValue,
  compareFunc,
  groupBy,
  useToggle,
} from 'nmqf-common-ui';

const AVAILABLE_VACCINE_GROUP_NAME = ['Influenza', 'Pneumococcal'];

const renderCptCodeColumn = (
  cptCode: string,
  row: LookupVaccine,
  rowIdx: number,
  extraData: [string[] | undefined, (event: React.ChangeEvent<HTMLInputElement>) => void]
) => {
  const [selectingVaccineCptCodes, onChange] = extraData;

  return (
    <FormGroup check>
      <SimpleCheckbox
        disabled={!AVAILABLE_VACCINE_GROUP_NAME.some(v => v === row.vaccineDiseaseName)}
        checked={selectingVaccineCptCodes?.some((v) => v === cptCode)}
        value={cptCode}
        label={cptCode}
        onChange={onChange}
      />
    </FormGroup>
  );
};

interface VaccineGroupProps {
  vaccineGroupName: string;
  data: LookupVaccine[];
}

const VaccineGroup: React.FC<VaccineGroupProps> = (props) => {
  const [showTable, toggleShowTable] = useToggle();
  const [lookupVaccine] = useLookupVaccine();
  const [vaccineCptCodes = [], setVaccineCptCodes] = useFilterEditingValue('vaccineCptCodes');
  const [textSearch] = useFilterEditingValue('textSearch');
  const selectingVaccineDiseaseName = lookupVaccine.data?.find((i) => i.cptCode === vaccineCptCodes[0])?.vaccineDiseaseName;
  const checked = selectingVaccineDiseaseName === props.vaccineGroupName && props.data.length === vaccineCptCodes.length;
  const indeterminate = selectingVaccineDiseaseName === props.vaccineGroupName && props.data.length !== vaccineCptCodes.length;
  const disabled = !AVAILABLE_VACCINE_GROUP_NAME.some(v => v === props.vaccineGroupName);

  const filteredOptions = useMemo(
    () =>
      props.data.filter(
        (i) =>
          !textSearch ||
          i.vaccineDiseaseName.toLowerCase().includes(textSearch.toLowerCase()) ||
          i.cptCode.toLowerCase().includes(textSearch.toLowerCase()) ||
          i.vaccineBrandName?.toLowerCase().includes(textSearch.toLowerCase()) ||
          i.cvxDescription?.toLowerCase().includes(textSearch.toLowerCase())
      ),
    [props.data, textSearch]
  );

  const handleChangeVaccineGroupCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setVaccineCptCodes(props.data.map((i) => i.cptCode));
    } else {
      setVaccineCptCodes();
    }
  };

  const handleChangeCptCodeCheckbox = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked, value } = event.target;
      const vaccineDiseaseName = lookupVaccine.data?.find((i) => i.cptCode === value)?.vaccineDiseaseName;
      if (checked && vaccineDiseaseName === selectingVaccineDiseaseName) {
        setVaccineCptCodes([value, ...vaccineCptCodes].sort(compareFunc));
      } else if (checked) {
        setVaccineCptCodes([value]);
      } else {
        setVaccineCptCodes(vaccineCptCodes.filter((v) => v !== value));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lookupVaccine.data, vaccineCptCodes, setVaccineCptCodes]
  );

  const formatExtraDataCptCodeColumn = useMemo(() => [vaccineCptCodes, handleChangeCptCodeCheckbox], [vaccineCptCodes, handleChangeCptCodeCheckbox]);

  if (!filteredOptions.length) return null;
  return (
    <div>
      <FormGroup check>
        <SimpleCheckbox disabled={disabled} checked={checked} indeterminate={indeterminate} onChange={handleChangeVaccineGroupCheckbox}>
          <Button color="link" className="p-0 mx-1" onClick={toggleShowTable}>
            <FontAwesomeIcon icon={!showTable ? 'caret-right' : 'caret-down'} />
          </Button>
          &nbsp;
          {props.vaccineGroupName}
        </SimpleCheckbox>
      </FormGroup>
      <div className={!showTable ? 'd-none' : ''}>
        <DataTable
          hover
          columns={[
            {
              dataField: 'cptCode',
              text: 'CPT',
              sort: true,
              headerStyle: { width: '20%' },
              formatter: renderCptCodeColumn,
              formatExtraData: formatExtraDataCptCodeColumn,
            },
            {
              dataField: 'vaccineBrandName',
              text: 'Brand',
              sort: true,
            },
            {
              dataField: 'cvxDescription',
              text: 'Description',
              sort: true,
              headerStyle: { width: '50%' },
            },
          ]}
          keyField="cptCode"
          data={filteredOptions}
        />
      </div>
    </div>
  );
};

export const VaccinesTab: React.FC<FilterDialogTabItemProps> = (props) => {
  const [lookupVaccine, fetchLookupVaccine] = useLookupVaccine();

  const vaccineOptions: Record<string, LookupVaccine[]> | undefined = useMemo(() => groupBy(lookupVaccine.data, 'vaccineDiseaseName'), [lookupVaccine.data]);

  return (
    <TabPane tabId={props.tabId}>
      <FallbackComponent requesting={lookupVaccine.requesting} error={lookupVaccine.error} retry={fetchLookupVaccine}>
        <Label className="font-weight-bold">Vaccines</Label>
        {vaccineOptions &&
          Object.entries(vaccineOptions).map(([vaccineGroupName, options]) => (
            <VaccineGroup key={vaccineGroupName} vaccineGroupName={vaccineGroupName} data={options} />
          ))}
      </FallbackComponent>
    </TabPane>
  );
};

VaccinesTab.defaultProps = {
  tabId: 'vaccinestab',
  tabName: 'Vaccines',
};
